import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useQuery } from '@apollo/client'
import { Dispatch } from 'redux'
import { AppState } from '@chegg-wt/core/state/types'
import { DashboardUpsellCopy } from '@chegg-wt/core/types/features'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { UseShowTourHandler } from '@chegg-wt/core/hooks/useShowTour'
import { useScreenSize } from '@chegg-wt/core/hooks/useScreenSize'
import { GetProjectCount } from '@chegg-wt/core/queries/GetProjectCount'
import { GET_PROJECT_COUNT } from '@chegg-wt/core/queries/Project'
import { Config } from '@chegg-wt/core/config'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { CitationsExperience } from '@chegg-wt/core/types/citation-flow'
import { initializedSelector } from '@chegg-wt/core/state/app/selectors'
import {
  isProSelector,
  isGuestSelector,
  loggedInSelector,
} from '@chegg-wt/core/state/user/selectors'
import { authModalExpFlagSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { ONE_AUTH_MODAL } from '@chegg-wt/core/state/modals/constants'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import { isParaphraseFlagEnabledSelector } from '@chegg-wt/core/state/paraphraser/selectors'
import CitationWidget from '../CitationWidget'
import { CitationPanel, ECPanel } from '../FeaturePanel'
import Tour from '../Tour'
import { ExpertCheckContainerPanelId } from '../FeaturePanel/domIds'
import { useRoutesContext } from '../RoutesContext'
import { UCFUploadWidget } from '../UCFUploadWidget'
import SEOTitle from '../SEOTitle'
import { AuthModalOrigin } from '../Modals/AuthModal/types'
import { EntryPointBanner } from '../ProofReader/EntryPointBanner/EntryPointBanner'
import CreateAccountCallout from './CreateAccountCallout'
import HeroBanner from './HeroBanner'
import PapersList from './PapersList'
import * as Styles from './styles'
import ProjectList from './ProjectList'
import { tourDashboardSteps } from './Tour/tourSetup'
import * as DomIds from './domIds'
import { UPLOAD_WIDGET, UPLOAD_WIDGET_H2_ID } from './utils/testIds'

export const DASHBOARD_HEADING = 'Dashboard'
export const WRITING_HEADING = 'Check your paper for grammar and plagiarism'
export const UPSELL_COPY_CITATION_CTA = 'to create unlimited citations'
export const UPSELL_COPY_PAPERS_CTA = 'to save your papers'

export interface DashboardProps {
  upsellCopy: DashboardUpsellCopy
  initialized: boolean
  isGuest: boolean
  isPro: boolean
  citationsExperience: CitationsExperience
  onUserClickSource: (value: string) => void
  onUserClickCTA: () => void
  onUserClickCreateAccountCitationWidgetCTA: () => void
  onUserClickSignInCitationWidgetCTA: () => void
  onUserClickCreateAccountPaperUploadWidgetCTA: () => void
  onUserClickSignInPaperUploadWidgetCTA: () => void
  onCheckMyPaperUploadWidgetCTA: () => void
  onComputerButtonUploadWidgetCTA: () => void
  onGoogleDriveUploadWidgetCTA: () => void
  tourHandler?: UseShowTourHandler
  isLoggedIn: boolean
  authModalExpFlag: boolean
  openModal: (payload: OpenModalPayload) => void
  isProofreaderEnabled: boolean
}

export const Dashboard: React.FC<DashboardProps> = ({
  upsellCopy,
  initialized,
  isGuest,
  isPro,
  citationsExperience,
  onUserClickSource,
  onUserClickCTA,
  onUserClickCreateAccountCitationWidgetCTA,
  onUserClickSignInCitationWidgetCTA,
  onUserClickCreateAccountPaperUploadWidgetCTA,
  onUserClickSignInPaperUploadWidgetCTA,
  onCheckMyPaperUploadWidgetCTA,
  onComputerButtonUploadWidgetCTA,
  onGoogleDriveUploadWidgetCTA,
  tourHandler,
  isLoggedIn,
  authModalExpFlag,
  isProofreaderEnabled,
  openModal,
}) => {
  const { data: countData } = useQuery<GetProjectCount>(GET_PROJECT_COUNT, {
    ssr: false,
    skip: !initialized,
    fetchPolicy: 'cache-and-network',
  })

  const projectCount = countData?.projectCount?.count
  const showUpsell = isGuest && !!projectCount

  const currentScreenSize = useScreenSize()
  const [currentTourStep, setCurrentTourStep] = useState(0)
  const tourSteps = tourDashboardSteps(isPro, !!citationsExperience, currentScreenSize)

  const { toDashboard } = useRoutesContext()

  if (!initialized)
    return <LoadingDots message="Loading dashboard" verticalPadding={60} />

  const openAuthModal = (defaultFlow: string) => {
    if (authModalExpFlag) {
      openModal({
        name: ONE_AUTH_MODAL,
        data: {
          defaultFlow,
          origin: AuthModalOrigin.DASHBOARD_PAGE_WIDGET,
        },
      })
    }
  }

  const onSignInClickCitation = () => {
    onUserClickSignInCitationWidgetCTA()
    openAuthModal('signin')
  }

  const onCreateAccountClickCitation = () => {
    onUserClickCreateAccountCitationWidgetCTA()
    openAuthModal('signup')
  }

  const onSignInClickPaper = () => {
    onUserClickSignInPaperUploadWidgetCTA()
    openAuthModal('signin')
  }

  const onCreateAccountClickPaper = () => {
    onUserClickCreateAccountPaperUploadWidgetCTA()
    openAuthModal('signup')
  }

  return (
    <>
      <SEOTitle title={`Dashboard | ${Config.siteName}`} />
      {isProofreaderEnabled !== null &&
        (isProofreaderEnabled ? (
          <Styles.EntryBannerContainer>
            <EntryPointBanner />
          </Styles.EntryBannerContainer>
        ) : (
          <HeroBanner />
        ))}

      <Styles.Main>
        <Styles.PanelsContainer>
          <Styles.CitationsPanelContainer>
            {citationsExperience ? (
              <>
                <CitationWidget
                  citationsExperience={citationsExperience}
                  onUserClickSource={onUserClickSource}
                  showUpsell={showUpsell}
                />
                <ProjectList citationsExperience={citationsExperience} />
                <CreateAccountCallout
                  onUserClickCreateAccountCTA={onCreateAccountClickCitation}
                  onUserClickSignInCTA={onSignInClickCitation}
                  copy={UPSELL_COPY_CITATION_CTA}
                  isLoggedIn={isLoggedIn}
                  projectCount={projectCount}
                  showAuthModal={authModalExpFlag}
                />
              </>
            ) : (
              <CitationPanel
                onClickCTA={onUserClickCTA}
                redirectTo={toDashboard()}
                upsellCopy={upsellCopy}
              />
            )}
          </Styles.CitationsPanelContainer>
          <Styles.CTAPanelContainer id={ExpertCheckContainerPanelId}>
            <ECPanel upsellCopy={upsellCopy} />
          </Styles.CTAPanelContainer>
          <Styles.WritingPanelContainer>
            <Styles.UploadWidgetContainer id={DomIds.UploadWidgetContainerId}>
              <Styles.UploadWidgetH2 data-test={UPLOAD_WIDGET_H2_ID}>
                {WRITING_HEADING}
              </Styles.UploadWidgetH2>
              <Styles.UploadWidgetRoot
                id={DomIds.UploadWidgetRoot}
                data-test={UPLOAD_WIDGET}
              >
                <UCFUploadWidget
                  onCtaClicked={onCheckMyPaperUploadWidgetCTA}
                  onComputerCtaClicked={onComputerButtonUploadWidgetCTA}
                  onGoogleDriveCtaClicked={onGoogleDriveUploadWidgetCTA}
                  pasteOnly={false}
                />
              </Styles.UploadWidgetRoot>
            </Styles.UploadWidgetContainer>
            <CreateAccountCallout
              onUserClickCreateAccountCTA={onCreateAccountClickPaper}
              onUserClickSignInCTA={onSignInClickPaper}
              copy={UPSELL_COPY_PAPERS_CTA}
              isLoggedIn={isLoggedIn}
              projectCount={projectCount}
              showAuthModal={authModalExpFlag}
            />
            <PapersList />
          </Styles.WritingPanelContainer>
        </Styles.PanelsContainer>
        {tourHandler?.active && (
          <Tour
            key={currentScreenSize}
            steps={tourSteps}
            stepIndex={currentTourStep}
            onNext={(index: number) => {
              setCurrentTourStep(index)
            }}
            onComplete={tourHandler?.hide}
          />
        )}
      </Styles.Main>
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  initialized: initializedSelector(state),
  isGuest: isGuestSelector(state),
  isLoggedIn: loggedInSelector(state),
  isPro: isProSelector(state),
  authModalExpFlag: authModalExpFlagSelector(state),
  isProofreaderEnabled: isParaphraseFlagEnabledSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onUserClickSource: (value) =>
    dispatch(userClick({ origin: origins.dashboardClickSource, value })),
  onUserClickCTA: () =>
    dispatch(userClick({ origin: origins.dashboardClickCitationCTA })),
  onUserClickCreateAccountCitationWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardCreateAccountCitationWidgetCTA })),
  onUserClickSignInCitationWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardSignInCitationWidgetCTA })),
  onUserClickCreateAccountPaperUploadWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardCreateAccountPaperUploadWidgetCTA })),
  onUserClickSignInPaperUploadWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardSignInPaperUploadWidgetCTA })),
  onCheckMyPaperUploadWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardCheckMyPaperUploadWidgetCTA })),
  onComputerButtonUploadWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardMyComputerUploadWidgetCTA })),
  onGoogleDriveUploadWidgetCTA: () =>
    dispatch(userClick({ origin: origins.dashboardGoogleDriveUploadWidgetCTA })),
  openModal: (payload) => dispatch(openModal(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
